import { useState, useRef, useEffect } from 'react';

export default function PiscinaBuscador({
  piscinas,
  value,
  onChange,
  disabled = false,
  placeholder = 'Escribe: Ps 05  o  1-10',
  label = 'Piscina',
  permitirTodas = false,
  valorTodas = '',
  labelTodas = 'Todas',
}) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  const seleccionada = piscinas.find((p) => p.id === value);
  const textoSeleccionado = seleccionada?.codigo
    || (permitirTodas && value === valorTodas ? labelTodas : null);

  // Filtrar piscinas por el texto de búsqueda
  const filtradas = piscinas.filter((p) => {
    if (!busqueda.trim()) return true;
    const q = busqueda.trim().toLowerCase();

    // Rango "1-10"
    const rangoMatch = q.match(/^(\d+)\s*-\s*(\d+)$/);
    if (rangoMatch) {
      const inicio = parseInt(rangoMatch[1]);
      const fin = parseInt(rangoMatch[2]);
      const numMatch = p.codigo.match(/(\d+)/);
      if (!numMatch) return false;
      const n = parseInt(numMatch[1]);
      return n >= inicio && n <= fin;
    }

    return p.codigo.toLowerCase().includes(q);
  });

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setAbierto(false);
        setBusqueda('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const abrirConFoco = () => {
    if (disabled) return;
    setAbierto(true);
    // Enfocar el input en el siguiente ciclo (cuando ya está visible)
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const seleccionar = (p) => {
    onChange(p.id);
    setBusqueda('');
    setAbierto(false);
    setTimeout(() => inputRef.current?.blur(), 30);
  };

  const limpiar = () => {
    onChange(permitirTodas ? valorTodas : '');
    setBusqueda('');
    // Dejar el foco en el input para escribir de inmediato
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  return (
    <div className="block relative" ref={ref}>
      <span className="text-xs text-gray-500">{label}</span>

      {/* Contenedor con posición relativa para superponer chip e input */}
      <div className="relative mt-1">
        {/* Input siempre presente (editable) */}
        <input
          ref={inputRef}
          type="text"
          value={busqueda}
          onChange={(e) => {
            setBusqueda(e.target.value);
            setAbierto(true);
          }}
          onFocus={() => setAbierto(true)}
          placeholder={
            disabled
              ? 'Selecciona un sector primero'
              : (textoSeleccionado ? '' : placeholder)
          }
          disabled={disabled}
          className={`
            w-full px-2.5 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30
            ${textoSeleccionado ? 'border-primary bg-blue-50 pr-14' : 'border-gray-300 bg-white'}
            disabled:bg-gray-100
          `}
        />

        {/* Chip visual superpuesto cuando hay selección y no se está escribiendo */}
        {textoSeleccionado && !busqueda && (
          <div
            className="absolute inset-0 flex items-center justify-between px-2.5 pointer-events-none"
          >
            <span className="font-medium text-sm text-gray-800 truncate">
              {textoSeleccionado}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                limpiar();
              }}
              className="text-gray-400 hover:text-red-500 text-xs pointer-events-auto"
              title="Quitar selección"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* Lista desplegable */}
      {abierto && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {permitirTodas && !busqueda.trim() && (
            <button
              type="button"
              onClick={() => seleccionar({ id: valorTodas })}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 font-medium ${
                value === valorTodas ? 'bg-blue-100' : ''
              }`}
            >
              🌐 {labelTodas}
            </button>
          )}

          {filtradas.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400 text-center">
              Sin resultados
            </div>
          ) : (
            filtradas.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => seleccionar(p)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between ${
                  p.id === value ? 'bg-blue-100 font-semibold' : ''
                }`}
              >
                <span>{p.codigo}{!p.activo ? ' (inactiva)' : ''}</span>
                {p.area_ha != null && (
                  <span className="text-xs text-gray-400">
                    {Number(p.area_ha).toFixed(2)} ha
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}